<template>
  <div class="view-container reference-profiles-page">
    <div class="extended-filter-block">
      <div class="back-button-wrapper" @click="goBack('/analytics/reports')">
        <LeftOutlined class="back-button" />
      </div>

      <h2>Эталонный профиль</h2>
    </div>
    <div class="card flex flex-column align-item-start">
      <div>
        <div class="access-info">
          <div class="access-info__item">
            <div>Доступно:</div>
            <div class="bold">
              {{ availableReferenceProfiles.length }}
            </div>
          </div>

          <a-divider class="access-info__divider" type="vertical" style="margin: 0 8px" />

          <div class="access-info__item">
            <div>Истекает:</div>
            <div class="bold">{{ availableReferenceProfiles.length && availableReferenceProfiles[0].expires_at ? availableReferenceProfiles[0].expires_at : '-' }}</div>
            <div v-if="availableReferenceProfiles.length && availableReferenceProfiles[0].expires_at" class="access-info__icon">
              <ClockCircleOutlined class="circle-clock" :style="{ color: '#FAAD14' }" />
            </div>
          </div>
        </div>
      </div>

      <div class="profile-description">
        <p>
          Определяет, какие параметры ЭИ связаны с высоким достижением KPI на конкретной должности, и формирует эталон компетенций на основе данных минимум 60 сотрудников. Позволяет сравнивать результаты сотрудников с эталоном и классифицировать их по уровню соответствия.
        </p>
      </div>

      <div class="buttons-container">
        <div class="left-buttons">
          <a-button
              class="primary-button"
              :disabled="!canCreateReference"
              @click="currentAction = 'create'"
          >
            <template #icon>
              <PlusOutlined />
            </template>

            Создать
          </a-button>

          <a-button class="secondary-button" @click="goToInstruction">
            Инструкция для загрузки
          </a-button>
        </div>

        <div class="right-buttons">
          <a-button class="secondary-button" @click="goToDetails">
            Подробнее на сайте
          </a-button>
        </div>
      </div>
    </div>

    <div class="card card__with-action">
      <TableHorizontalScrollShell ref="tableShell">
        <a-table
            ref="tableRef"
            row-key="id"
            class="references-table"
            :columns="columns"
            :data-source="references"
            :scroll="tableScroll"
            :pagination="false"
            :loading="tableLoading"
            :row-selection="{ selectedRowKeys: selectedReferences, onChange: onSelectChange, columnWidth: 40}"
            :table-layout="'fixed'"
            @change="handleTableChange"
        >
          <template #bodyCell="{ column, text, record }">
            <template v-if="column.dataIndex === 'title'">
              <a
                  class="green-text"
                  @click="goToReference(record)"
              >
                {{ text }}
              </a>
            </template>

            <template v-if="column.dataIndex === 'users_count'">
              <a
                  class="green-text"
                  @click="goToSample(record)"
              >
                {{ text ?? 0 }}
              </a>
            </template>

            <template v-if="column.dataIndex === 'matchPercentage'">
              <a
                  class="green-text"
                  @click="goToMatches(record)"
              >
                Смотреть
              </a>
            </template>

            <template v-if="column.dataIndex === 'settings'">
              <a-dropdown :trigger="['click']" placement="bottomLeft">
                <a-button type="text" @click.prevent>
                  <MenuOutlined />
                </a-button>

                <template #overlay>
                  <a-menu @click="({ key }) => handleAction(key, record)">
                    <a-menu-item
                        v-for="action in actions"
                        :key="action.id"
                    >
                      {{ action.name }}
                    </a-menu-item>
                  </a-menu>
                </template>
              </a-dropdown>
            </template>
          </template>

          <template #emptyText>
            <div class="no-data-wrapper">
              <img
                  :src="NotFoundIcon"
                  class="no-data-image"
                  alt="Нет данных"
              />

              <div class="no-data-text">
                Нет данных для отображения.
              </div>

              <a-button @click="fetchReferences">
                <template #icon>
                  <ReloadOutlined />
                </template>

                Обновить
              </a-button>
            </div>
          </template>
        </a-table>
      </TableHorizontalScrollShell>

      <div class="bottom-container">
        <div class="action-block reference-profiles-page__actions" v-if="!isMobile || isMobile && selectedUsers.length">
          <div class="action-select-wrapper">
            <a-select
                v-model:value="actionToApply"
                placeholder="Выберите действие"
                style="width: 100%;"
                :not-found-content="'Нет данных'"
                :disabled="!selectedReferences.length"
                @change="() => handleAction(actionToApply)"
            >
              <a-select-option
                  v-for="action in groupActions"
                  :key="action.id"
                  :value="action.id"
              >
                {{ action.name }}
              </a-select-option>
            </a-select>
          </div>
        </div>

        <a-pagination
            v-model:current="currentPage"
            :page-size="entitiesPerPage"
            :buildOptionText="count => `${count.value} / стр`"
            :show-total="isMobile ? undefined : (total => `Всего ${total ?? 0} элементов`)"
            :total="totalProfiles"
            :show-less-items="isMobile"
            @change="handlePageChange"
        />
      </div>
    </div>

    <EditReference
        v-if="currentAction === 'rename'"
        :open="currentAction === 'rename'"
        :reference="editReference"
        @submitted="fetchAfterSubmit"
        @close="clearCurrentAction"
    />

    <DeleteReferenceProfile
        v-if="currentAction === 'delete'"
        :open="currentAction === 'delete'"
        :references="profilesToDelete"
        @submitted="fetchAfterSubmit"
        @close="clearCurrentAction"
    />

    <ReferenceProfileEditForm
        v-if="currentAction === 'create' || currentAction === 'add_employee'"
        :open="currentAction === 'create' || currentAction === 'add_employee'"
        :profileId="editReference?.id ?? null"
        :profileTitle="editReference?.title ?? null"
        @close="clearCurrentAction"
        @referenceCreated="fetchAfterReportUpdate"
        @referenceUpdated="fetchAfterReportUpdate"
    />
  </div>
</template>

<script>
import { computed, nextTick, onBeforeMount, ref, watch } from 'vue'
import { useStore } from 'vuex'
import {onBeforeRouteLeave, useRouter} from 'vue-router'
import { message } from 'ant-design-vue'
import {
  ClockCircleOutlined,
  PlusOutlined,
  MenuOutlined,
  ReloadOutlined, LeftOutlined,
} from '@ant-design/icons-vue'

import NotFoundIcon from '@/assets/images/icons/no_data.svg'
import TableHorizontalScrollShell from '@/components/Tables/TableScrollComponent.vue'
import { useTableColumns } from '@/mixins/useTableColumns'
import { TABLE_SCROLL_Y } from '@/mixins/useTableScroll'

import EditReference from '@/feature/references/EditReference.vue'
import ReferenceProfileEditForm from '../../feature/references/ReferenceProfileEditForm.vue'
import DeleteReferenceProfile from "@/feature/references/DeleteReferenceProfile.vue";

export default {
  name: 'reference-profiles-list',

  components: {
    LeftOutlined,
    TableHorizontalScrollShell,
    EditReference,
    ReferenceProfileEditForm,
    DeleteReferenceProfile,

    ClockCircleOutlined,
    PlusOutlined,
    MenuOutlined,
    ReloadOutlined,
  },

  setup() {
    const store = useStore();
    const router = useRouter();

    const tableShell = ref(null);
    const tableLoading = ref(false);

    const currentAction = ref(null);
    const actionToApply = ref(null);
    const editReference = ref({});
    const profilesToDelete = ref([]);

    const selectedReferences = ref([]);

    const currentPage = ref(1);
    const entitiesPerPage = ref(10);
    const availableReferenceProfiles = computed(() => store.state.references.availableProfiles);

    const profile = computed(() => store.state.profile.profile);

    const canCreateReference = computed(() => {
      return Number(availableReferenceProfiles.value.length > 0);
    })

    const initialColumns = ref([
      {
        title: 'Название',
        dataIndex: 'title',
        sorter: true,
        visible: true,
        width: '40%',
      },
      {
        title: 'Выборка',
        dataIndex: 'users_count',
        sorter: true,
        visible: true,
        width: '13%',
      },
      {
        title: 'Совпадения',
        dataIndex: 'matchPercentage',
        sorter: false,
        visible: true,
        width: '15%',
      },
      {
        title: 'Дата создания',
        dataIndex: 'calculated_at',
        sorter: true,
        visible: true,
        width: '15%',
      },
      {
        title: 'Дата изменения',
        dataIndex: 'updated_at',
        sorter: true,
        visible: true,
        width: '17%',
      },
    ])

    const { columns, scrollX, scrollY, allColumns, tableRef, tableScroll, updateTableYScroll } = useTableColumns(
        initialColumns,
        {
          tableKey: 'references',
          settingsWidth: 40,
          selectionWidth: 40,
          width: 900,
          scrollYSize: TABLE_SCROLL_Y.twoCards,
        },
    )

    const actions = [
      {
        id: 'download',
        name: 'Скачать',
      },
      {
        id: 'add_employee',
        name: 'Добавить сотрудников',
      },
      {
        id: 'view_matches',
        name: 'Смотреть совпадения',
      },
      {
        id: 'view_sample',
        name: 'Смотреть выборку',
      },
      {
        id: 'rename',
        name: 'Переименовать',
      },
      {
        id: 'delete',
        name: 'Удалить',
      },
    ]

    const groupActions = [
      {
        id: 'delete',
        name: 'Удалить',
      },
    ]

    const sortState = ref({
      field: null,
      order: null,
    })

    const profilesData = computed(() => {
      return store.state.references.calculatedProfiles?.data ?? []
    })

    const totalProfiles = computed(() => {
      return store.state.references.calculatedProfiles?.total ?? 0
    })

    const references = computed(() => {
      return (profilesData.value ?? []).map((item) => {
        return {
          ...item,
          title: item.title ?? item.name ?? '',
          users_count: item.users_count ?? 0,
          created_at: item.created_at ?? '',
          updated_at: item.updated_at_formatted ?? '',
        }
      })
    })

    const requestParams = computed(() => {
      return {
        per_page: entitiesPerPage.value,
        page: currentPage.value,
        sort_field: sortState.value.field,
        sort_direction:
            sortState.value.order === 'ascend'
                ? 'asc'
                : sortState.value.order === 'descend'
                    ? 'desc'
                    : null,
      }
    })

    const fetchReferences = () => {
      tableLoading.value = true

      return store.dispatch('references/getCalculatedProfiles', requestParams.value)
          .finally(() => {
            tableLoading.value = false
          });
    }

    const handleTableChange = (pagination, filters, sorter) => {
      sortState.value = {
        field: sorter?.field ?? null,
        order: sorter?.order ?? null,
      }

      currentPage.value = 1
      selectedReferences.value = []

      fetchReferences()
    }

    const retrieveEntitiesPerPage = () => {
      const storageValue = localStorage.getItem('perPage')
      entitiesPerPage.value = storageValue ? Number(storageValue) : 10
    }

    const setEntitiesPerPage = (num) => {
      localStorage.setItem('perPage', num)
    }

    const handlePageChange = (current, perPage) => {
      if (entitiesPerPage.value !== perPage) {
        entitiesPerPage.value = perPage
        setEntitiesPerPage(entitiesPerPage.value)
      }

      currentPage.value = current
      selectedReferences.value = []

      fetchReferences()
    }

    const onSelectChange = (selectedRowKeys) => {
      selectedReferences.value = selectedRowKeys
    }

    const clearCurrentAction = () => {
      currentAction.value = null
      actionToApply.value = null
      editReference.value = {}
      profilesToDelete.value = [];
    }

    const fetchAfterSubmit = () => {
      clearCurrentAction();
      selectedReferences.value = [];
      fetchReferences()
    }

    const fetchAfterReportUpdate = () => {
      fetchReferences();
    }

    const handleAction = (action, record = null) => {
      if (!action) {
        return
      }

      if (!record && !selectedReferences.value.length) {
        message.error('Выберите эталонный профиль')
        actionToApply.value = null
        return
      }

      if (action === 'rename') {
        currentAction.value = action
        editReference.value = {
          ...(record ?? {}),
        }

        return
      }

      if (action === 'view_matches') {
        goToMatches(record)
        return
      }

      if (action === 'view_sample') {
        goToSample(record)
        return
      }

      if (action === 'add_employee') {
        currentAction.value = action
        editReference.value = {
          ...(record ?? {}),
        }
        return
      }

      if (action === 'download') {
        downloadReference(record)
        return
      }

      if (action === 'delete') {
        if (record && record.id) {
          profilesToDelete.value.push(record.id);
        } else {
          profilesToDelete.value = selectedReferences.value;
        }
      }

      currentAction.value = action;
    }

    const fetchAvailableReferenceProfiles = () => {
      store.dispatch('references/getAvailableProfiles');
    }

    const goToReference = (record) => {
      router.push(`/profiles/report/${record.id}`)
    }

    const goToSample = (record) => {
      router.push(`/profiles/detail/${record.id}/users`);
    }

    const goToMatches = (record) => {
      router.push(`/profiles/report/${record.id}/users`);
    }

    const downloadReference = (record) => {
        store.dispatch('analytics/getReferenceProfileReport', record.id)
        message.info('Файл генерируется, пожалуйста, подождите')
    }

    const goToInstruction = () => {
      //
    }

    const goToDetails = () => {
      window.open('https://way2wei.ru/product/otcet-etalonnyi-ei-profil-sotrudnikarukovoditelya', '_blank');
    }

    const goBack = (url) => {
      if (window.history.length > 1) {
        router.back()
      } else {
        router.push(url)
      }
    }

    onBeforeMount(() => {
      retrieveEntitiesPerPage();
      fetchReferences();
      fetchAvailableReferenceProfiles();
    })

    onBeforeRouteLeave((to) => {
      const isStillInProfiles = to.path.startsWith('/profiles')

      if (!isStillInProfiles) {
        store.commit('references/clearListState')
      }
    });

    watch(tableLoading, async (loading) => {
      if (!loading) {
        await tableShell.value?.rebindScroll()
        await updateTableYScroll()
      }
    })

    watch(references, async () => {
      await nextTick()
      await tableShell.value?.rebindScroll()
      await updateTableYScroll()
    }, { deep: true })

    watch(allColumns, async () => {
      await nextTick()
      await tableShell.value?.rebindScroll()
      await updateTableYScroll()
    }, { deep: true })

    watch(
        [currentPage, entitiesPerPage, sortState],
        () => {
          store.commit('references/setListState', {
            currentPage: currentPage.value,
            sortField: sortState.value.field,
            sortOrder: sortState.value.order,
          })
        },
        { deep: true }
    )

    return {
      router,
      NotFoundIcon,

      tableShell,
      tableRef,
      tableScroll,
      tableLoading,

      canCreateReference,

      columns,
      scrollX,
      scrollY,

      references,
      totalProfiles,

      actions,
      groupActions,
      selectedReferences,
      actionToApply,

      currentAction,
      editReference,

      currentPage,
      entitiesPerPage,

      fetchReferences,
      handleTableChange,
      handlePageChange,
      onSelectChange,
      handleAction,

      clearCurrentAction,
      fetchAfterSubmit,

      goToReference,
      goToSample,
      goToMatches,
      goToInstruction,
      goToDetails,

      availableReferenceProfiles,
      profilesToDelete,

      goBack,
      fetchAfterReportUpdate
    }
  },
}
</script>

<style scoped lang="scss">
.reference-profiles-page {
  padding-top: 0;
  padding-bottom: 0;
}

.card__with-action {
  margin-bottom: 0;
}

.access-info {
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  font-weight: 400;
  font-size: 14px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.65);


  .bold {
    font-weight: 600;
    font-size: 14px;
    line-height: 120%;
    color: var(--main-font)
  }
}

.access-info__item,
.access-info__icon {
  display: flex;
  align-items: center;
}

.access-info__item {
  gap: 4px;
}

.access-info__divider {
  margin: 0 2px;
}

.circle-clock {
  display: flex;
  align-items: center;
}

.buttons-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 16px;
  gap: 16px;
  width: 100%;

  @media (max-width: $mobile-breakpoint) {
    align-items: stretch;
    flex-direction: column;
  }
}

.left-buttons {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;

  @media (max-width: $mobile-breakpoint) {
    width: 100%;

    .ant-btn {
      flex: 1;
    }
  }
}

.right-buttons {
  display: flex;

  @media (max-width: $mobile-breakpoint) {
    width: 100%;

    .ant-btn {
      width: 100%;
    }
  }
}

.reference-profiles-page__actions {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.action-select-wrapper {
  min-width: 180px;

  @media (max-width: $mobile-breakpoint) {
    width: 100%;

    .ant-select {
      width: 100%;
    }
  }
}

.green-text {
  cursor: pointer;
  color: var(--main-green);
}

.green-text:hover {
  color: var(--hover-green);
}

.no-data-wrapper {
  min-height: 320px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 6px;
  padding: 24px;
  text-align: center;
}

.no-data-text {
  color: var(--main-font);
}

.no-data-wrapper button {
  margin-top: 10px;
}

.no-data-wrapper button:hover {
  border-color: var(--main-green);
  color: var(--main-green);
}

:deep(.ant-btn) {
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
}

:deep(.ant-table-wrapper .ant-table-thead > tr > th) {
  padding: 5px !important;
}

:deep(.ant-table-wrapper .ant-table-tbody > tr:not(.ant-table-measure-row) > td) {
  padding: 10px 5px !important;
}

:deep(.ant-table-wrapper .ant-table-tbody > tr.ant-table-row-selected > td) {
  background-color: var(--light-grey) !important;
}

:deep(.ant-table-wrapper .ant-table-tbody > tr.ant-table-row-selected:hover > td) {
  background-color: var(--light-grey) !important;
}

:deep(.ant-btn-text.ant-dropdown-trigger) {
  color: lightgrey;
  height: auto !important;
  padding: 0 !important;
}

:deep(.ant-btn-text.ant-dropdown-trigger.ant-dropdown-open) {
  color: var(--main-font);
}

:deep(.anticon-reload) {
  cursor: pointer;
}

:deep(.anticon-reload:hover) {
  color: var(--main-green) !important;
}

.profile-description {
  max-width: 800px;
  font-weight: 400;
  font-size: 14px;
  line-height: 120%;

  p {
    padding: 0;
    margin: 0;
  }
}

:deep(.references-table .ant-table-tbody>tr:not(.ant-table-measure-row) .ant-checkbox-wrapper) {
  height: 18px !important;
}

:deep(.ant-table-wrapper .ant-table-tbody>tr:not(.ant-table-measure-row)>td:has(.ant-dropdown-trigger)),
:deep(.ant-table-wrapper .ant-table-tbody>tr:not(.ant-table-measure-row)>td:has(.ant-checkbox-wrapper)) {
  padding: 10px 5px !important;
}

:deep(.references-table .ant-table-header table colgroup col:nth-child(1)),
:deep(.references-table .ant-table-body table colgroup col:nth-child(1)) {
  width: 30px !important;
  min-width: 30px !important;
  max-width: 30px !important;
}

:deep(.references-table .ant-table-header table colgroup col:nth-child(2)),
:deep(.references-table .ant-table-body table colgroup col:nth-child(2)){
  width: 35px !important;
  min-width: 35px !important;
  max-width: 35px !important;
}

.extended-filter-block {
  display: none;

  @media (max-width: $mobile-breakpoint) {
    margin: 24px 20px;
    display: flex;
    column-gap: 12px;
    align-items: center;
  }
}

.extended-filter-block h2 {
  font-size: 20px;
  margin-bottom: 0;
  font-weight: 600;
  line-height: 120%;
}

.back-button-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  min-width: 24px;
  min-height: 24px;
  background-color: #ffffff;
  border-radius: 25%;
  cursor: pointer;
}

:deep(.references-table .ant-table-header) {
  scrollbar-gutter: stable;
}

:deep(.references-table .ant-table-body) {
  scrollbar-gutter: stable;
}
</style>
